import { zodResolver } from "@hookform/resolvers/zod";

import { Loader2, Trash } from "lucide-react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  type SessionFormDto,
  createSessionDtoSchema,
  type TagDto,
  type SpeakerDto,
} from "slde-api/dto-schemas";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from "@/components/ui/combobox";
import { useResources } from "@/providers/resources.provider";
import React, { useState } from "react";

interface SessionFormProps {
  defaultValues?: SessionFormDto & { categories?: TagDto[] };
  onSubmit: (values: SessionFormDto) => void;

  disabled?: boolean;
  loading?: boolean;
}
export function SessionForm({
  onSubmit: onSubmitProps,
  defaultValues,
  disabled,
  loading,
}: SessionFormProps) {
  const {
    tags,
    createTag,
    isCreatingTag,
    deleteTag,
    isDeletingTag,
    speakers,
    createSpeaker,
    isCreatingSpeaker,
    deleteSpeaker,
    isDeletingSpeaker,
  } = useResources();
  const [tagInput, setTagInput] = useState("");
  const [moderatorInput, setModeratorInput] = useState("");
  const tagsAnchor = useComboboxAnchor();
  const moderatorsAnchor = useComboboxAnchor();

  const form = useForm<SessionFormDto>({
    resolver: zodResolver(createSessionDtoSchema),
    defaultValues: defaultValues
      ? {
          ...defaultValues,
          tagIds: defaultValues.categories?.map((cat: TagDto) => cat.id),
          moderatorIds: defaultValues.moderators?.map(
            (mod: SpeakerDto) => mod.id,
          ),
        }
      : {},
  });

  function onSubmit(values: SessionFormDto) {
    onSubmitProps(values);
  }

  return (
    <div className="flex gap-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field: { value, ...field } }) => (
                <FormItem>
                  <FormLabel>Nazwa</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Sesja poranna - Zaawansowane techniki"
                      value={value ?? ""}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field: { value, ...field } }) => (
                <FormItem>
                  <FormLabel>Opis</FormLabel>
                  <FormControl>
                    <Textarea placeholder="" value={value ?? ""} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="moderatorIds"
              render={({ field: { value, onChange } }) => {
                return (
                  <FormItem>
                    <FormLabel>Moderatorzy</FormLabel>
                    <FormControl>
                      <Combobox
                        items={speakers}
                        multiple
                        value={value ?? []}
                        onValueChange={onChange}
                        autoHighlight
                      >
                        <ComboboxChips
                          ref={moderatorsAnchor}
                          className="w-full"
                        >
                          <ComboboxValue>
                            {(values) => (
                              <React.Fragment>
                                {speakers
                                  .filter((speaker) =>
                                    values?.includes(speaker.id),
                                  )
                                  .map((speaker: SpeakerDto) => (
                                    <ComboboxChip key={speaker.id}>
                                      {speaker.fullName}
                                    </ComboboxChip>
                                  ))}
                                <ComboboxChipsInput
                                  onChange={(e) =>
                                    setModeratorInput(e.target.value)
                                  }
                                />
                              </React.Fragment>
                            )}
                          </ComboboxValue>
                        </ComboboxChips>
                        <ComboboxContent anchor={moderatorsAnchor}>
                          <ComboboxEmpty>
                            <div className="flex flex-col items-center gap-2">
                              <span>Brak wyników</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={isCreatingSpeaker || !moderatorInput}
                                onClick={() =>
                                  createSpeaker({ fullName: moderatorInput })
                                }
                              >
                                {isCreatingSpeaker && (
                                  <Loader2 className="animate-spin" />
                                )}
                                Dodaj &ldquo;{moderatorInput}&rdquo;
                              </Button>
                            </div>
                          </ComboboxEmpty>
                          <ComboboxList>
                            {(speaker: SpeakerDto) => (
                              <ComboboxItem key={speaker.id} value={speaker.id}>
                                <div className="flex items-center justify-between w-full">
                                  <span> {speaker.fullName}</span>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="link"
                                    disabled={isDeletingSpeaker}
                                    onClick={(e) => {
                                      deleteSpeaker({ id: speaker.id });
                                    }}
                                  >
                                    {isDeletingSpeaker && (
                                      <Loader2 className="animate-spin" />
                                    )}
                                    <Trash />
                                  </Button>
                                </div>
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <FormField
              control={form.control}
              name="tagIds"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Kategorie</FormLabel>
                  <FormControl>
                    <Combobox
                      items={tags}
                      multiple
                      value={value ?? []}
                      onValueChange={onChange}
                      autoHighlight
                    >
                      <ComboboxChips ref={tagsAnchor} className="w-full">
                        <ComboboxValue>
                          {(values) => (
                            <React.Fragment>
                              {tags
                                .filter((tag) => values?.includes(tag.id))
                                .map((tag: TagDto) => (
                                  <ComboboxChip key={tag.id}>
                                    {tag.name}
                                  </ComboboxChip>
                                ))}
                              <ComboboxChipsInput
                                onChange={(e) => setTagInput(e.target.value)}
                              />
                            </React.Fragment>
                          )}
                        </ComboboxValue>
                      </ComboboxChips>
                      <ComboboxContent anchor={tagsAnchor}>
                        <ComboboxEmpty>
                          <div className="flex flex-col items-center gap-2">
                            <span>Brak wyników</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={isCreatingTag || !tagInput}
                              onClick={() => createTag({ name: tagInput })}
                            >
                              {isCreatingTag && (
                                <Loader2 className="animate-spin" />
                              )}
                              Dodaj &ldquo;{tagInput}&rdquo;
                            </Button>
                          </div>
                        </ComboboxEmpty>
                        <ComboboxList>
                          {(tag: TagDto) => (
                            <ComboboxItem key={tag.id} value={tag.id}>
                              <div className="flex items-center justify-between w-full">
                                <span>{tag.name}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="link"
                                  disabled={isDeletingTag}
                                  onClick={() => deleteTag({ id: tag.id })}
                                >
                                  {isDeletingTag && (
                                    <Loader2 className="animate-spin" />
                                  )}
                                  <Trash />
                                </Button>
                              </div>
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button type="submit" disabled={disabled || loading}>
            {!!defaultValues ? "Edytuj" : "Dodaj"}
            {loading && <Loader2 className="animate-spin" />}
          </Button>
        </form>
      </Form>
    </div>
  );
}
